import { createReducer } from '@reduxjs/toolkit';

import { BaseTaskTemplate, WorkflowTemplate, WorkflowTemplateGroup } from 'data/models/WorkflowTemplates';
import {
  createTaskTemplateAction,
  createWorkflowTemplateGroupAction,
  createWorkflowTemplateAction,
  loadWorkflowTemplatesAction,
  removeTaskTemplateAction,
  removeWorkflowTemplateGroupAction,
  removeWorkflowTemplateAction,
  resetSelectedWorkflowTemplateAction,
  resetWorkflowTemplateGroupFormAction,
  setWorkflowTemplateAction,
  setWorkflowTemplatesLoading,
  updateTaskTemplateAction,
  updateWorkflowTemplateAction,
  updateWorkflowTemplateGroupAction,
} from 'data/actions/workflowTemplates';

interface State {
  workflowTemplates: WorkflowTemplate[];
  workflowTemplateGroupForm: WorkflowTemplateGroup[];
  selectedWorkflowTemplate: WorkflowTemplate | undefined;
  isWorkflowTemplatesLoading: boolean;
}

const initialState: State = {
  workflowTemplates: [],
  workflowTemplateGroupForm: [
    {
      tasksGroup: [],
    },
  ],
  selectedWorkflowTemplate: undefined,
  isWorkflowTemplatesLoading: false,
};

export default createReducer(initialState, builder => {
  builder.addCase(setWorkflowTemplatesLoading, (state, action) => ({
    ...state,
    isWorkflowTemplatesLoading: action.payload,
  }));

  builder.addCase(loadWorkflowTemplatesAction, (state, action) => {
    state.workflowTemplates = [...action.payload];
  });

  builder.addCase(createWorkflowTemplateAction, (state, action) => {
    state.workflowTemplates = [...state.workflowTemplates, action.payload];
  });

  builder.addCase(updateWorkflowTemplateAction, (state, action) => {
    state.workflowTemplates = state.workflowTemplates.map(workflow => (workflow.id === action.payload.id ? action.payload : workflow));
  });

  builder.addCase(removeWorkflowTemplateAction, (state, action) => {
    state.workflowTemplates = state.workflowTemplates.filter(workflow => workflow.id !== action.payload);
    state.selectedWorkflowTemplate = initialState.selectedWorkflowTemplate;
    state.workflowTemplateGroupForm = initialState.workflowTemplateGroupForm;
  });

  builder.addCase(setWorkflowTemplateAction, (state, action) => {
    const foundWorkflowTemplate = state.workflowTemplates.find(workflow => workflow.id === action.payload);
    if (!foundWorkflowTemplate) return state;
    state.selectedWorkflowTemplate = foundWorkflowTemplate;
    state.workflowTemplateGroupForm = [...foundWorkflowTemplate.groups];
  });

  builder.addCase(resetSelectedWorkflowTemplateAction, (state, action) => {
    state.selectedWorkflowTemplate = initialState.selectedWorkflowTemplate;
  });

  builder.addCase(createTaskTemplateAction, (state, action) => {
    if (!state.workflowTemplateGroupForm[action.payload.groupIndex]) return state;
    state.workflowTemplateGroupForm[action.payload.groupIndex].tasksGroup.push(action.payload.taskTemplate);
  });

  builder.addCase(updateTaskTemplateAction, (state, action) => {
    if (!state.workflowTemplateGroupForm[action.payload.groupIndex]) return state;
    state.workflowTemplateGroupForm[action.payload.groupIndex].tasksGroup = state.workflowTemplateGroupForm[action.payload.groupIndex].tasksGroup.map(
      (task: BaseTaskTemplate) => {
        if ('id' in task && 'id' in action.payload.taskTemplate) {
          return task.id === action.payload.taskTemplate.id ? action.payload.taskTemplate : task;
        } else if ('temporaryUuid' in task && 'temporaryUuid' in action.payload.taskTemplate) {
          return task.temporaryUuid === action.payload.taskTemplate.temporaryUuid ? action.payload.taskTemplate : task;
        }
        return task;
      },
    );
  });

  builder.addCase(removeTaskTemplateAction, (state, action) => {
    if (!state.workflowTemplateGroupForm[action.payload.groupIndex]) return state;
    state.workflowTemplateGroupForm[action.payload.groupIndex].tasksGroup = state.workflowTemplateGroupForm[
      action.payload.groupIndex
    ].tasksGroup.filter(task => {
      if (action.payload.id && task.id) {
        return task.id !== action.payload.id;
      } else if (action.payload.temporaryUuid && task.temporaryUuid) {
        return task.temporaryUuid !== action.payload.temporaryUuid;
      }
      return true;
    });
  });

  builder.addCase(createWorkflowTemplateGroupAction, (state, action) => {
    const newGroup = {
      tasksGroup: [],
    };

    state.workflowTemplateGroupForm = [...state.workflowTemplateGroupForm, newGroup];
  });

  builder.addCase(updateWorkflowTemplateGroupAction, (state, action) => {
    state.workflowTemplateGroupForm = action.payload;
  });

  builder.addCase(removeWorkflowTemplateGroupAction, (state, action) => {
    state.workflowTemplateGroupForm = state.workflowTemplateGroupForm.filter((group, index) => index !== action.payload);
  });

  builder.addCase(resetWorkflowTemplateGroupFormAction, (state, action) => {
    state.workflowTemplateGroupForm = initialState.workflowTemplateGroupForm;
  });
});
